import { motion } from 'framer-motion';
import { Users, Heart, Truck, Sparkles } from 'lucide-react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';

const TEAM_PHOTO_URL = "https://customer-assets.emergentagent.com/job_sip-schedule-admin/artifacts/980hpql4_image.png";

export const AboutPage = () => {
  return (
    <div className="min-h-screen bg-white" data-testid="about-page">
      <Navbar />
      
      {/* Header */}
      <section className="bg-gradient-to-r from-sip-blue to-sip-pink py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <Users className="w-16 h-16 mx-auto mb-4 text-white" />
            <h1 className="font-script text-4xl md:text-6xl text-white mb-4">
              About Us
            </h1>
            <p className="text-xl text-white/90 max-w-2xl mx-auto">
              The story behind the sips!
            </p>
          </motion.div>
        </div>
      </section>

      {/* Team Photo Section */}
      <section className="py-12 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
            className="text-center"
          >
            <div className="relative inline-block">
              <img
                src={TEAM_PHOTO_URL}
                alt="The Sip Fantasia Team - Chad, Danni, and Alicia in front of the Sip Fantasia trailer"
                className="w-full max-w-2xl h-auto rounded-2xl shadow-2xl mx-auto"
                data-testid="team-photo"
              />
              <div className="absolute -bottom-4 left-1/2 -translate-x-1/2 bg-white px-6 py-2 rounded-full shadow-lg">
                <p className="font-script text-sip-navy text-lg">Meet the Team!</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-script text-3xl md:text-5xl text-sip-navy mb-8">
              Our Story
            </h2>
            
            <div className="text-lg text-gray-700 space-y-6 leading-relaxed">
              <p className="text-xl font-semibold text-gray-900">
                Sip Fantasia was born from friendship, family, and a lifelong love for serving others.
              </p>
              
              <p>
                In 1998, Chad and Danni met while working at a local pizza joint. What started as a workplace friendship quickly became something much deeper - but not before years of laughter, teamwork, and shared dreams. Long before they were husband and wife, they were best friends who discovered they worked incredibly well side by side.
              </p>
              
              <p>
                Fast forward 28 years: a marriage, two amazing kids, and several different career paths later, their passion for the food service industry and their community never faded. Throughout the years, one dream stayed constant - to be their own bosses and build something meaningful together. With their children now grown and following their own paths, Chad and Danni knew the time had finally come to chase that dream.
              </p>
              
              <p>
                In 2016, Danni met Alicia while working at a children's consignment store. Alicia, a proud mom of two fantastic and wonderful daughters, instantly connected with Danni. Their shared values, work ethic, and passion for customer service created an immediate bond. It didn't take long to realize they had the same goals - to create something joyful, community-centered, and service-driven. Naturally, Alicia became an essential part of bringing Sip Fantasia to life.
              </p>
              
              <p>
                Together, the three of them bring over 50 years of combined customer service experience in food service and retail. From early mentors in the industry, they learned a lesson that became the heart of their business:
              </p>
              
              <p className="text-2xl font-bold text-sip-navy text-center py-6 font-script">
                The guest truly does come first!
              </p>
              
              <p>
                That philosophy isn't just a motto - it's how they operate every single day. Whether serving up a refreshing drink, creating a fun event atmosphere, or connecting with their community, Sip Fantasia is built on warmth, hospitality, and genuine care.
              </p>
              
              <p>
                Sip Fantasia isn't just a beverage cart - it's a dream years in the making, fueled by friendship, family and a passion for creating memorable experiences.
              </p>
              
              <p className="text-xl font-semibold text-gray-900 text-center pt-4">
                We can't wait to serve you!
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Mission Statement */}
      <section className="py-16 bg-gradient-to-r from-sip-yellow to-sip-orange">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <h2 className="font-script text-3xl md:text-4xl text-gray-900 mb-6">
              Our Mission
            </h2>
            <p className="text-lg md:text-xl text-gray-800 leading-relaxed max-w-3xl mx-auto">
              Our mission is to spark joy in our community by serving bold, imaginative beverages that bring people together. We believe work should feel like play, flavor should inspire curiosity, and every visit to our cart should create a moment worth savoring. Through creativity, connection, and a whole lot of fun, we aim to refresh our neighborhood one unforgettable sip at a time.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="font-script text-3xl md:text-4xl text-sip-navy text-center mb-12"
          >
            What We Stand For
          </motion.h2>
          
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: Heart,
                title: 'Made with Love',
                description: 'Every drink is handcrafted with care and passion. We put our heart into every cup we serve.',
                color: 'bg-sip-pink'
              },
              {
                icon: Sparkles,
                title: 'Good Vibes. Great Sips!',
                description: "We believe every sip should come with a smile. Our drinks are crafted to brighten your day and create moments worth sharing. Life's too short for boring beverages!",
                color: 'bg-sip-blue'
              },
              {
                icon: Truck,
                title: 'Community First',
                description: "We're not just a business – we're your neighbors. We love being part of local events and celebrations.",
                color: 'bg-sip-navy'
              }
            ].map((value, index) => {
              const Icon = value.icon;
              return (
                <motion.div
                  key={value.title}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="card-graffiti text-center"
                  data-testid={`value-card-${index}`}
                >
                  <div className={`${value.color} w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4`}>
                    <Icon className="text-white" size={28} />
                  </div>
                  <h3 className="font-semibold text-xl text-gray-900 mb-2">{value.title}</h3>
                  <p className="text-gray-600">{value.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="card-featured"
          >
            <h2 className="font-script text-3xl md:text-4xl text-gray-900 mb-4">
              Come Say Hi!
            </h2>
            <p className="text-xl text-gray-600 mb-6">
              We'd love to meet you! Check our schedule to find out where we'll be next, 
              or book us for your upcoming event.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <a href="/schedule" className="btn-primary">
                Find Us
              </a>
              <a href="/events" className="btn-secondary">
                Book an Event
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AboutPage;
